// HeroSection.jsx — drop-in replacement (Industry dark wireframe).
// Same public contract as the old file: default export `HeaderHeroSection`,
// uses react-router `useNavigate` and `authService`, and lucide-react icons.
// Self-contained: only inline styles + one scoped <style> block, so it works
// regardless of your Tailwind config. Needs the Barlow fonts loaded (see INSTALL.md).

import { TrendingUp, ArrowRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';

const C = {
  bg: '#15171a', surface: '#1e2126', text: '#eceef0',
  dim: 'rgba(236,238,240,0.62)', faint: 'rgba(236,238,240,0.42)',
  divider: 'rgba(236,238,240,0.15)', neutral: 'rgba(236,238,240,0.10)',
  accent: '#5980a6', pos: '#46a07a', posInk: '#86c9a6', neg: '#cf6f61', negInk: '#e59a8f',
  head: '"Barlow Condensed", "Barlow", system-ui, sans-serif',
  body: '"Barlow", system-ui, -apple-system, sans-serif',
};

const Corner = ({ pos }) => {
  const b = `1px solid ${C.accent}`;
  const s = { position: 'absolute', width: 7, height: 7, [pos.includes('t') ? 'top' : 'bottom']: -1, [pos.includes('l') ? 'left' : 'right']: -1 };
  if (pos.includes('t')) s.borderTop = b; else s.borderBottom = b;
  if (pos.includes('l')) s.borderLeft = b; else s.borderRight = b;
  return <i style={s} />;
};

const Row = ({ label, value, color, tick, bottom, atTop, live }) => (
  <div style={{ position: 'absolute', left: 0, right: 0, ...(atTop ? { top: 4, transform: 'translateY(-50%)' } : { bottom, transform: 'translateY(50%)' }), display: 'flex', alignItems: 'center', gap: live ? 0 : 8 }}>
    {live ? (
      <>
        <span style={{ flex: 1, height: 1.5, background: C.accent }} />
        <span style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: C.bg, background: C.accent, padding: '2px 6px' }}>{value}</span>
      </>
    ) : (
      <>
        <span style={{ width: 8, height: 1, background: tick, flex: 'none' }} />
        <span style={{ font: `600 9px/1 ${C.head}`, letterSpacing: '0.1em', color }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: color || C.text }}>{value}</span>
      </>
    )}
  </div>
);

const HeaderHeroSection = () => {
  const navigate = useNavigate();
  const isAuthenticated = authService.isAuthenticated();

  const handleGetStarted = () => navigate(isAuthenticated ? '/dashboard' : '/register');
  const handleViewTrades = () => {
    const el = document.getElementById('trades');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="xau-hero" style={{ background: C.bg, color: C.text, fontFamily: C.body, borderBottom: `1px solid ${C.divider}` }}>
      <style>{`
        @keyframes xauPulse { 0%,100%{opacity:1} 50%{opacity:.2} }
        .xau-hero .xau-dot { animation: xauPulse 1.8s ease-in-out infinite; }
        .xau-hero .xau-btn-primary:hover { background:#7ba0c4 !important; color:#15171a !important; }
        .xau-hero .xau-btn-ghost:hover { border-color:#5980a6 !important; background:rgba(89,128,166,0.14) !important; }
        @media (max-width: 900px){ .xau-hero .xau-grid{ grid-template-columns:1fr !important; gap:40px !important; } }
      `}</style>

      <div className="xau-grid" style={{ maxWidth: 1200, margin: '0 auto', padding: '76px 32px', display: 'grid', gridTemplateColumns: '1.08fr 0.92fr', gap: 56, alignItems: 'center' }}>
        {/* Copy */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <span style={{ font: `600 12px/1 ${C.head}`, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.accent }}>XAU/USD · Gold signals</span>
          <h1 style={{ fontFamily: C.head, fontSize: 'clamp(38px, 5vw, 56px)', lineHeight: 1.02, margin: 0, letterSpacing: '-0.02em', fontWeight: 700 }}>
            The levels drawn,<br />not described.
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.5, margin: 0, maxWidth: 480, color: C.dim }}>
            Every gold signal ships with entry, stop and target plotted to scale — plus live P&amp;L in pips as price moves. No hype, no chat noise. Just the trade.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
            <button onClick={handleGetStarted} className="xau-btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: C.accent, color: C.text, border: 'none', borderRadius: 2, font: `600 15px/1 ${C.head}`, letterSpacing: '0.04em', cursor: 'pointer' }}>
              {isAuthenticated ? 'Go to dashboard' : 'Start free'}<ArrowRight size={16} />
            </button>
            <button onClick={handleViewTrades} className="xau-btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 22px', background: 'transparent', color: C.text, border: `1px solid ${C.divider}`, borderRadius: 2, font: `600 15px/1 ${C.head}`, letterSpacing: '0.04em', cursor: 'pointer' }}>
              <Eye size={16} />View trades
            </button>
          </div>
          <div style={{ display: 'flex', gap: 26, marginTop: 14, paddingTop: 22, borderTop: `1px solid ${C.divider}`, fontVariantNumeric: 'tabular-nums', flexWrap: 'wrap' }}>
            {[['71.8%', 'Win rate · 30d', C.text], ['+1,246', 'Pips · MTD', C.posInk], ['1 : 2.1', 'Avg R : R', C.text]].map(([n, l, col]) => (
              <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontFamily: C.head, fontSize: 24, fontWeight: 700, lineHeight: 1, color: col }}>{n}</span>
                <span style={{ font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.dim }}>{l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Live signal figure */}
        <article style={{ position: 'relative', border: `1px solid ${C.divider}`, background: C.surface, padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Corner pos="tl" /><Corner pos="tr" /><Corner pos="bl" /><Corner pos="br" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 9px', background: C.accent, color: C.text, font: `600 11px/1 ${C.head}`, letterSpacing: '0.06em' }}><TrendingUp size={12} />BUY</span>
            <span style={{ font: `600 16px/1 ${C.head}`, letterSpacing: '0.02em' }}>XAU/USD</span>
            <span style={{ padding: '3px 8px', background: C.neutral, font: `600 10px/1 ${C.head}`, letterSpacing: '0.04em', color: C.text }}>Breakout</span>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, letterSpacing: '0.06em', color: C.posInk }}><span className="xau-dot" style={{ width: 6, height: 6, background: C.pos, display: 'inline-block' }} />LIVE · 2h14m</span>
          </div>
          <div style={{ display: 'flex', gap: 18 }}>
            {/* ladder */}
            <div style={{ position: 'relative', width: 160, height: 168, flex: 'none' }}>
              <div style={{ position: 'absolute', left: 3, top: 4, bottom: 4, width: 1, background: C.divider }} />
              <div style={{ position: 'absolute', left: 2, width: 3, bottom: '35%', height: '46%', background: C.pos }} />
              <Row atTop label="TP" value="2724.00" color={C.posInk} tick={C.accent} />
              <Row live value="2718.40" bottom="81%" />
              <Row label="ENTRY" value="2700.00" color={C.dim} tick="rgba(236,238,240,0.45)" bottom="35%" />
              <Row label="SL" value="2688.00" color={C.negInk} tick={C.neg} bottom={4} />
            </div>
            {/* readout */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontFamily: C.head, fontSize: 38, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: C.posInk }}>+184</span>
                <span style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>pips · unrealized</span>
              </div>
              <div style={{ display: 'flex', gap: 20, fontVariantNumeric: 'tabular-nums' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>R : R</span><span style={{ fontSize: 15, fontWeight: 600 }}>1 : 2.0</span></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.dim }}>To Target</span><span style={{ fontSize: 15, fontWeight: 600, color: C.posInk }}>77%</span></div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ height: 6, background: C.neutral, position: 'relative' }}><div style={{ position: 'absolute', inset: '0 23% 0 0', background: C.pos }} /></div>
                <span style={{ fontSize: 10, color: C.posInk, letterSpacing: '0.02em' }}>Approaching take-profit</span>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default HeaderHeroSection;
